import type { ReactNode } from "react";
import { twMerge } from "tailwind-merge";

import type { ButtonBaseProps } from "./Button";
import type { InputProps } from "./Input";
import Searchbar from "./Searchbar";
import Select, { type BaseSelectProps } from "./Select";
import type { SelectItem } from "./types";

type FilterValue = string | number;

type SearchFilterBarBaseFilter<T extends FilterValue = FilterValue> = {
  items: readonly SelectItem<T>[];
  selectedKey?: T | null;
  placeholder: string;
  ariaLabel: string;
  buttonProps?: ButtonBaseProps;
  className?: string;
  classNames?: Exclude<BaseSelectProps["classNames"], undefined>;
  dismissable?: boolean;
};

type SearchFilterBarDismissableFilter<T extends FilterValue = FilterValue> =
  SearchFilterBarBaseFilter<T> & {
    dismissable: true;
    onSelectionChange: (key: T | null) => void;
  };

type SearchFilterBarFixedFilter<T extends FilterValue = FilterValue> =
  SearchFilterBarBaseFilter<T> & {
    dismissable?: false;
    onSelectionChange: (key: T) => void;
  };

export type SearchFilterBarFilter<T extends FilterValue = FilterValue> =
  | SearchFilterBarDismissableFilter<T>
  | SearchFilterBarFixedFilter<T>;

export type SearchFilterBarProps = {
  readonly searchbarProps: InputProps;
  readonly filters?: readonly SearchFilterBarFilter[];
  readonly children?: ReactNode;
  readonly className?: string;
};
const defaultFilterButtonProps: ButtonBaseProps = {
  size: "md",
  color: "tertiary",
  variant: "ghost-light-bordered",
};

export default function SearchFilterBar({
  searchbarProps,
  filters = [],
  children,
  className,
}: SearchFilterBarProps) {
  return (
    <div
      className={twMerge(
        "bg-secondary/80 flex items-center gap-3 rounded-lg p-2 shadow-sm",
        className,
      )}
    >
      <Searchbar
        // a placeholder alone gives the input no accessible name
        aria-label={searchbarProps["aria-label"] ?? searchbarProps.placeholder}
        {...searchbarProps}
      />

      {filters.map((filter) =>
        filter.dismissable ? (
          <Select
            key={filter.ariaLabel}
            isClearable
            aria-label={filter.ariaLabel}
            items={filter.items}
            value={filter.selectedKey ?? null}
            onChange={filter.onSelectionChange}
            placeholder={filter.placeholder}
            className={filter.className}
            classNames={filter.classNames}
            buttonProps={{
              ...defaultFilterButtonProps,
              ...filter.buttonProps,
              className: twMerge(
                defaultFilterButtonProps.className,
                filter.buttonProps?.className,
              ),
            }}
          />
        ) : (
          <Select
            key={filter.ariaLabel}
            aria-label={filter.ariaLabel}
            items={filter.items}
            value={filter.selectedKey ?? null}
            onChange={filter.onSelectionChange}
            placeholder={filter.placeholder}
            className={filter.className}
            classNames={filter.classNames}
            buttonProps={{
              ...defaultFilterButtonProps,
              ...filter.buttonProps,
              className: twMerge(
                defaultFilterButtonProps.className,
                filter.buttonProps?.className,
              ),
            }}
          />
        ),
      )}

      {children}
    </div>
  );
}
